import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, Injectable, LOCALE_ID, PLATFORM_ID, inject } from '@angular/core';

import { FIRST_PARTY_CONFIG, type IFirstPartyConfig } from './first-party.config';
import { VisitorIdentityService } from './visitor-identity.service';

// Событие в буфере до отправки. Ключи конверта (`params`) — snake_case wire-контракт
// каталога; PII не кладём по дизайну.
export interface IQueuedEvent {
    readonly name: string;
    readonly occurredAt: string;
    readonly pagePath?: string;
    readonly params?: Record<string, unknown>;
}

interface IBatchEnvelope {
    readonly visitorId: string;
    readonly sessionId: string;
    readonly app: string;
    readonly locale: string;
    readonly events: readonly IQueuedEvent[];
}

/**
 * Батчинг и доставка первопартийных событий на edge `/collect`. Флаш по размеру батча,
 * по таймеру и при уходе со страницы (`pagehide`/`visibilitychange:hidden`). Транспорт —
 * `navigator.sendBeacon` (переживает выгрузку вкладки), с фолбэком на `fetch keepalive`.
 * Best-effort: сетевые ошибки глотаются, аналитика не влияет на UX. SSR-safe.
 */
@Injectable({ providedIn: 'root' })
export class EventQueueService {
    readonly #isBrowser: boolean = isPlatformBrowser(inject(PLATFORM_ID));
    readonly #config: IFirstPartyConfig = inject(FIRST_PARTY_CONFIG);
    readonly #identity: VisitorIdentityService = inject(VisitorIdentityService);
    readonly #locale: string = inject(LOCALE_ID);
    readonly #buffer: IQueuedEvent[] = [];
    #timer: ReturnType<typeof setInterval> | null = null;

    constructor() {
        if (!this.#isBrowser) {
            return;
        }
        const flush: () => void = (): void => {
            this.flush();
        };
        globalThis.addEventListener('pagehide', flush);
        globalThis.addEventListener('visibilitychange', this.#onVisibilityChange);
        this.#timer = globalThis.setInterval(flush, this.#config.flushIntervalMs);
        inject(DestroyRef).onDestroy((): void => {
            globalThis.removeEventListener('pagehide', flush);
            globalThis.removeEventListener('visibilitychange', this.#onVisibilityChange);
            if (this.#timer !== null) {
                globalThis.clearInterval(this.#timer);
            }
        });
    }

    public enqueue(event: IQueuedEvent): void {
        if (!this.#isBrowser) {
            return;
        }
        this.#buffer.push(event);
        if (this.#buffer.length >= this.#config.maxBatchSize) {
            this.flush();
        }
    }

    public flush(): void {
        if (!this.#isBrowser || this.#buffer.length === 0) {
            return;
        }
        const session: string = this.#identity.ensureSession().sessionId;
        const envelope: IBatchEnvelope = {
            visitorId: this.#identity.visitorId(),
            sessionId: session,
            app: this.#config.app,
            locale: this.#locale,
            events: this.#buffer.splice(0, this.#buffer.length),
        };
        this.#send(envelope);
    }

    readonly #onVisibilityChange: () => void = (): void => {
        if (globalThis.document.visibilityState === 'hidden') {
            this.flush();
        }
    };

    #send(envelope: IBatchEnvelope): void {
        const body: string = JSON.stringify(envelope);
        if (this.#sendBeacon(body)) {
            return;
        }
        void globalThis
            .fetch(this.#config.collectUrl, {
                body,
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                keepalive: true,
                credentials: 'omit',
            })
            .catch((): void => undefined);
    }

    #sendBeacon(body: string): boolean {
        const beacon: Navigator['sendBeacon'] = globalThis.navigator.sendBeacon.bind(globalThis.navigator);
        return beacon(this.#config.collectUrl, new Blob([body], { type: 'application/json' }));
    }
}
